/**
 * When to drain.
 *
 * Separated from sync.ts because none of this can run in Node: it is browser event
 * wiring, and it is thin on purpose so that the decisions it triggers stay in tested
 * code.
 *
 * The set is redundant by design, because the two platforms fail differently:
 *
 *   Android/Chrome  Background Sync wakes the service worker with the tab closed.
 *   iOS Safari      has never supported Background Sync, and there is no shim. It
 *                   gets foreground drains, a poll while visible, and a pagehide
 *                   beacon carrying metadata only.
 *
 * Even so, the honest backstop is server-side: a manager should learn that an
 * inspection is missing because the server noticed, not because a phone reported in.
 * Anything that depends on an operator reopening the app will eventually lose a
 * record.
 */

import type { InspectDb } from './idb-schema.ts';
import { pendingCount } from './outbox-store.ts';
import { drainOnce, type Transport } from './sync.ts';

/** Long enough not to drain a battery, short enough to clear a queue during a shift. */
const POLL_INTERVAL_MS = 15_000;

const BACKGROUND_SYNC_TAG = 'outbox-drain';

export type StopSync = () => void;

export function startSyncEngine(db: InspectDb, transport: Transport): StopSync {
  const drain = () => {
    void drainOnce(db, transport);
  };

  const stopListening = listenForOpportunities(drain);
  const stopPolling = pollWhileVisible(db, drain);

  drain();
  void requestBackgroundSync();

  return () => {
    stopListening();
    stopPolling();
  };
}

function listenForOpportunities(drain: () => void): StopSync {
  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      drain();
    }
  };

  document.addEventListener('visibilitychange', onVisible);
  // pageshow catches a restore from the back/forward cache, which iOS uses constantly
  // and which fires no other event the app can see.
  window.addEventListener('pageshow', drain);
  window.addEventListener('online', drain);

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pageshow', drain);
    window.removeEventListener('online', drain);
  };
}

/**
 * Polls only while the tab is visible AND something is waiting. A timer running on a
 * hidden tab with an empty queue is pure battery cost, and an operator whose phone
 * dies mid-shift stops inspecting.
 */
function pollWhileVisible(db: InspectDb, drain: () => void): StopSync {
  const timer = setInterval(() => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    void pendingCount(db).then((waiting) => {
      if (waiting > 0) {
        drain();
      }
    });
  }, POLL_INTERVAL_MS);

  return () => {
    clearInterval(timer);
  };
}

/**
 * Android only, and a silent no-op everywhere else. Never depended upon.
 */
async function requestBackgroundSync(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker?.ready;
    const sync = (registration as { sync?: { register: (tag: string) => Promise<void> } })?.sync;
    await sync?.register(BACKGROUND_SYNC_TAG);
  } catch {
    // Unsupported, or blocked by a device policy. The foreground triggers remain.
  }
}
